import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAssetPath } from '../utils/paths';
import {
  MessageCircle,
  MessageSquare,
  ShoppingCart,
  Settings,
  User,
  Bot,
  BadgeQuestionMark as Help,
  Info,
  Phone,
  Mail,
  Moon,
  Sun,
  LogOut,
  ChevronDown,
  Languages,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthProvider';
import { useTheme } from 'chat-interface-lib';
import { useI18n } from '../contexts/I18nProvider';
import { useLocalizedEnv } from '../hooks/useLocalizedEnv';
import type { AgentTranslation, IconName } from '../types';
import { apiService } from '../services/api';
import { useTranslation } from '@/hooks/useTranslation';
import { useTenantConfig } from '../hooks/useTenantConfig';
import { useTenantIcons } from '../hooks/useTenantIcons';
import * as LucideIcons from 'lucide-react';

export const AgentTopBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [agents, setAgents] = useState<Array<{ id: string; name: string; description: string; icon: string; color: string, translations?: AgentTranslation[] }>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { theme, toggleTheme } = useTheme();
  const { logout, userName } = useAuth();
  const { currentLanguage, availableLanguages, changeLanguage, isLoading: i18nLoading } = useI18n();
  const { getVar, getBoolean } = useLocalizedEnv();

  const [isAgentsDropdownOpen, setIsAgentsDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const agentsDropdownRef = useRef<HTMLDivElement>(null);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const [languages, setLanguages] = useState<Array<{ id: string; code: string; isDefault?: boolean }>>([]);

  // Get tenant configuration for logo
  const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'default';
  const { tenantData } = useTenantConfig(tenantSlug);
  const { getIcon } = useTenantIcons(tenantSlug);

  // Get icon components
  const BotIcon = getIcon('bot', 'Bot') || LucideIcons.Bot;
  const SettingsIcon = getIcon('settings', 'Settings') || LucideIcons.Settings;
  const UserIcon = getIcon('user', 'User') || LucideIcons.User;
  const LogOutIcon = getIcon('logout', 'LogOut') || LucideIcons.LogOut;
  const ChevronDownIcon = getIcon('chevronDown', 'ChevronDown') || LucideIcons.ChevronDown;
  const LanguageIcon = getIcon('language', 'Languages') || LucideIcons.Languages;
  const MoonIcon = getIcon('themeToggle', 'Moon') || LucideIcons.Moon;
  const SunIcon = Sun; // Always use Sun for light mode
  const MessageCircleIcon = getIcon('messageCircle', 'MessageCircle') || LucideIcons.MessageCircle;
  const MessageSquareIcon = getIcon('messageSquare', 'MessageSquare') || LucideIcons.MessageSquare;
  const ShoppingCartIcon = getIcon('shoppingCart', 'ShoppingCart') || LucideIcons.ShoppingCart;
  const HelpIcon = getIcon('help', 'HelpCircle') || Help;
  const InfoIcon = getIcon('info', 'Info') || LucideIcons.Info;
  const PhoneIcon = getIcon('phone', 'Phone') || LucideIcons.Phone;
  const MailIcon = getIcon('mail', 'Mail') || LucideIcons.Mail;

  // Icon mapping for agent icons
  const iconMap: Record<IconName, React.ComponentType<any>> = {
    MessageCircle: MessageCircleIcon,
    MessageSquare: MessageSquareIcon,
    ShoppingCart: ShoppingCartIcon,
    Settings: SettingsIcon,
    User: UserIcon,
    Bot: BotIcon,
    Help: HelpIcon,
    Info: InfoIcon,
    Phone: PhoneIcon,
    Mail: MailIcon,
  };
  
  // Get customization values from tenant config (fallback to env vars for backward compatibility)
  const companyName = tenantData?.config?.companyName || getVar('VITE_MULTICHAT_COMPANY_NAME', 'Techmer PM');
  const logoBase64 = tenantData?.config?.logoBase64 || import.meta.env.VITE_MULTICHAT_LOGO_BASE64;
  const logoPath = tenantData?.config?.logoPath || import.meta.env.VITE_MULTICHAT_LOGO_PATH || '/techmer-logo.jpg';
  const useTextLogo = tenantData?.config?.useTextLogo ?? getBoolean('VITE_MULTICHAT_USE_TEXT_LOGO', false);
  
  // Logo enhancement settings with defaults
  const logoLightBrightness = tenantData?.config?.logoLightBrightness || '1.0';
  const logoLightContrast = tenantData?.config?.logoLightContrast || '1.0';
  const logoLightDropShadowOpacity = tenantData?.config?.logoLightDropShadowOpacity || '0.0';
  const logoDarkBrightness = tenantData?.config?.logoDarkBrightness || '1.1';
  const logoDarkContrast = tenantData?.config?.logoDarkContrast || '1.1';
  const logoDarkDropShadowOpacity = tenantData?.config?.logoDarkDropShadowOpacity || '0.1';

  const isActive = (agentId: string) => location.pathname === `/agent/${agentId}`;

  const handleAgentSelect = (agentId: string) => {
    navigate(`/agent/${agentId}`);
    setIsAgentsDropdownOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLanguageChange = async (language: string) => {
    try {
      await changeLanguage(language);
      setIsLanguageDropdownOpen(false);
    } catch (error) {
      console.error('Language change failed:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    // Load agents for the tenant
    const loadAgents = async () => {
      const user = apiService.getStoredUser();
      if (!user?.tenantId) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        const languages = await apiService.getTenantLanguages(user.tenantId)
        setLanguages(languages);
 
        setLoading(true);
        setError(null);
        const list = await apiService.getTenantAgents(user.tenantId);
        
        setAgents(list);
      } catch (e) {
        setError('Failed to load agents');
      } finally {
        setLoading(false);
      }
    };
    loadAgents();
  }, [currentLanguage]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (agentsDropdownRef.current && !agentsDropdownRef.current.contains(event.target as Node)) {
        setIsAgentsDropdownOpen(false);
      }
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper function to render agent dropdown items
  const renderAgentDropdownItem = useCallback((agent: typeof agents[0]) => {
    const IconComponent = iconMap[agent.icon as IconName] || MessageCircleIcon;
    const active = isActive(agent.id);
    const currentLanguageId = languages.find(l => l.code === currentLanguage || l.isDefault)?.id;
    const agentTranslation = agent.translations?.find(t => t.languageId === currentLanguageId);
    const agentName = agentTranslation?.name || agent.name;
    const agentDescription = agentTranslation?.description || agent.description;

    return (
      <button
        key={agent.id}
        onClick={() => handleAgentSelect(agent.id)}
        className={`flex items-center space-x-3 w-full px-3 py-2 text-left hover:bg-muted transition-colors rounded-md ${
          active ? 'bg-primary/10 text-primary border-l-2 border-primary' : 'text-foreground'
        }`}
        title={agent.description}
      >
        <div
          className="p-1 rounded"
          style={{
            backgroundColor: active ? 'transparent' : `${agent.color}20`,
          }}
        >
          <IconComponent
            className="w-4 h-4"
            style={{
              color: active ? 'currentColor' : agent.color
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-medium truncate ${active ? 'text-primary' : 'text-foreground'}`}>
            {agentName}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {agentDescription}
          </div>
        </div>
        {active && (
          <div className="w-2 h-2 bg-primary rounded-full"></div>
        )}
      </button>
    );
  }, [currentLanguage, languages]);

  // Render logo or company text
  const renderLogo = () => {
    const logoElements: React.ReactNode[] = [];
    const isDarkMode = theme === 'dark';

    // Get filter values based on theme
    const brightness = isDarkMode ? logoDarkBrightness : logoLightBrightness;
    const contrast = isDarkMode ? logoDarkContrast : logoLightContrast;
    const dropShadowOpacity = isDarkMode ? logoDarkDropShadowOpacity : logoLightDropShadowOpacity;
    const shadowColor = isDarkMode ? '255, 255, 255' : '0, 0, 0';

    // Build filter string
    const filterParts: string[] = [];
    if (parseFloat(brightness) !== 1.0) {
      filterParts.push(`brightness(${brightness})`);
    }
    if (parseFloat(contrast) !== 1.0) {
      filterParts.push(`contrast(${contrast})`);
    }
    if (parseFloat(dropShadowOpacity) > 0) {
      filterParts.push(`drop-shadow(0 1px 2px rgba(${shadowColor}, ${dropShadowOpacity}))`);
    }
    const filterStyle = filterParts.length > 0 ? filterParts.join(' ') : undefined;

    // Add logo image if available (base64 takes precedence over URL)
    if (logoBase64) {
      logoElements.push(
        <div
          key="logo-base64-wrapper"
          className="relative inline-flex items-center"
        >
          {/* Subtle background for transparent logos in dark mode */}
          {isDarkMode && (
            <div className="absolute inset-0 bg-background/30 rounded-sm blur-sm -z-10" />
          )}
          <img
            key="logo-base64"
            src={logoBase64}
            alt={`${companyName} Logo`}
            className="h-8 w-auto object-contain transition-all"
            style={filterStyle ? { filter: filterStyle } : undefined}
          />
        </div>
      );
    } else if (logoPath) {
      logoElements.push(
        <div
          key="logo-path-wrapper"
          className="relative inline-flex items-center"
        >
          {/* Subtle background for transparent logos in dark mode */}
          {isDarkMode && (
            <div className="absolute inset-0 bg-background/30 rounded-sm blur-sm -z-10" />
          )}
          <img
            key="logo-path"
            src={logoPath.startsWith('http') ? logoPath : getAssetPath(logoPath)}
            alt={`${companyName} Logo`}
            className="h-8 w-auto object-contain transition-all"
            style={filterStyle ? { filter: filterStyle } : undefined}
            onError={(e) => {
              // Hide image on error, show text if available
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      );
    }

    // Add text logo if enabled
    if (useTextLogo && companyName) {
      logoElements.push(
        <span key="text-logo" className="text-xl font-bold text-foreground">
          {companyName}
        </span>
      );
    }

    // If no logo configured, show default or translation key
    if (logoElements.length === 0) {
      return (
        <span className="text-xl font-bold text-foreground">
          {companyName || t('login.companyName')}
        </span>
      );
    }

    return <>{logoElements}</>;
  };

  return (
    <div className="bg-card border-b border-border shadow-sm">
      <div className="flex items-center px-4 py-3">
        {/* Logo and Title */}
        <button
          onClick={() => navigate('/')}
          className="focus:outline-none"
          title={t('common.goToHome')}
          style={{ background: 'none', border: 'none', padding: 0, marginRight: '0.75rem' }}
        >
          {renderLogo()}
        </button>
        <h1 className="text-xl font-bold text-foreground flex-1 text-center">
          {t('common.multiAgentChat')}
        </h1>

        {/* Navigation */}
        <nav className="flex items-center space-x-2">
          {/* Language Selector */}
          <div className="relative" ref={languageDropdownRef}>
            <button
              onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
              className="flex items-center space-x-1 p-2 hover:bg-muted rounded-md transition-colors"
              title={t('common.selectLanguage')}
              disabled={i18nLoading}
            >
              <LanguageIcon className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {availableLanguages[currentLanguage] || currentLanguage}
              </span>
              <ChevronDownIcon className={`w-3 h-3 text-muted-foreground transition-transform duration-200 ${
                isLanguageDropdownOpen ? 'rotate-180' : ''
              }`} />
            </button>

            {/* Language Dropdown Menu */}
            {isLanguageDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-card border border-border rounded-lg shadow-lg z-50 py-1">
                <div className="max-h-48 overflow-y-auto">
                  {Object.entries(availableLanguages).map(([langCode, langName]) => (
                    <button
                      key={langCode}
                      onClick={() => handleLanguageChange(langCode)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                        currentLanguage === langCode ? 'bg-accent text-accent-foreground' : 'text-foreground'
                      }`}
                    >
                      {langName}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Dark Mode Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 hover:bg-muted rounded-md transition-colors"
            title={theme === 'dark' ? t('common.switchToLightMode') : t('common.switchToDarkMode')}
          >
            {theme === 'dark' ? (
              <SunIcon className="w-4 h-4 text-muted-foreground" />
            ) : (
              <MoonIcon className="w-4 h-4 text-muted-foreground" />
            )}
          </button>

          {/* User Info and Logout */}
          <div className="flex items-center space-x-2">
            {userName && (
              <span className="text-sm text-muted-foreground px-2">
                {userName}
              </span>
            )}
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-muted rounded-md transition-colors"
              title={t('common.signOut')}
            >
              <LogOutIcon className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center px-3 py-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="px-3 py-2 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}

          {/* Agents Dropdown */}
          <div className="relative" ref={agentsDropdownRef}>
            <button
              onClick={() => setIsAgentsDropdownOpen(!isAgentsDropdownOpen)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium border hover:bg-muted text-foreground border-transparent hover:border-border"
              title={t('common.selectAgent')}
            >
              <BotIcon className="w-4 h-4" />
              <span>{t('common.agents')}</span>
              <ChevronDownIcon className={`w-3 h-3 transition-transform duration-200 ${
                isAgentsDropdownOpen ? 'rotate-180' : ''
              }`} />
            </button>

            {/* Agents Dropdown Menu */}
            {isAgentsDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-80 bg-card border border-border rounded-lg shadow-lg z-50 py-2">
                <div className="max-h-96 overflow-y-auto">
                  {agents.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                      {t('common.noAgentsAvailable')}
                    </div>
                  ) : (
                    agents.map((agent) => (
                      <div key={agent.id} className="px-2">
                        {renderAgentDropdownItem(agent)}
                      </div>
                    ))
                  )}
                </div>
                {agents.length > 0 && (
                  <div className="border-t border-border px-4 py-2">
                    <div className="text-xs text-muted-foreground text-center">
                      {agents.length} {agents.length === 1 ? t('common.agent') : t('common.agents_plural')} {t('common.available')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}; 